import { AssignDroneRequest } from '../request-models/assign-drone-request.model';
import { ReturnDroneRequest } from '../request-models/return-drone-request.model';
import { RevokeDroneRequest } from '../request-models/revoke-drone-request.model';
import { UpdateDroneLeaseRequest } from '../request-models/update-drone-lease-request.model';
import { omitUndefined } from './wire-value-utils';

/** POST /admin/drones/:id/assign body: only the fields the hub reads, and only the ones that are set. */
export function toAssignDroneRequest(fields: AssignDroneRequest): AssignDroneRequest {
  return omitUndefined<AssignDroneRequest>({
    organizationId: fields.organizationId,
    assignedToProfileId: fields.assignedToProfileId,
    assignmentType: fields.assignmentType,
    notes: fields.notes,
  });
}

/** POST /admin/drones/:id/return body: only `notes`, when set. */
export function toReturnDroneRequest(fields: ReturnDroneRequest): ReturnDroneRequest {
  return omitUndefined<ReturnDroneRequest>({ notes: fields.notes });
}

/** POST /admin/drones/:id/revoke body: only `reason`. */
export function toRevokeDroneRequest(fields: RevokeDroneRequest): RevokeDroneRequest {
  return { reason: fields.reason };
}

/**
 * POST /admin/drones/:id/lease body. The hub replaces the whole lease, so a leased body sends every
 * lease field, with `null` for an empty termination or notes. A demo has no lease and sends none.
 */
export function toUpdateDroneLeaseRequest(fields: UpdateDroneLeaseRequest): UpdateDroneLeaseRequest {
  if (fields.assignmentType !== 'leased') {
    return { organizationId: fields.organizationId, assignmentType: fields.assignmentType };
  }
  return {
    organizationId: fields.organizationId,
    assignmentType: fields.assignmentType,
    leaseStatus: fields.leaseStatus ?? null,
    leaseStart: calendarDate(fields.leaseStart),
    leaseEnd: calendarDate(fields.leaseEnd),
    leaseTermination: calendarDate(fields.leaseTermination),
    leaseNotes: fields.leaseNotes?.trim() || null,
  };
}

/**
 * A lease date as the request documents it, "YYYY-MM-DD": the date part of a `date` column's wire value
 * ("2026-03-01T00:00:00.000Z", e.g. when `returnToService` re-saves a stored lease) or of a form value.
 * Empty becomes `null`; anything else passes through for the hub to reject.
 */
function calendarDate(value: string | null | undefined): string | null {
  if (!value) return null;
  return /^\d{4}-\d{2}-\d{2}(T|$)/.test(value) ? value.slice(0, 10) : value;
}
